/**
 * B2B 共通の**利用申請フォーム**（各コンソールの `GET/POST /apply`）。
 *
 * 2026-09-06 までは申請フォームが kalori.jp の LP（React `B2BApplyForm`）にあり、コンソールは
 * 招待メールを踏んだ人しか来ない面だった。それだと「LP を見た → 申請した → 承認 → 初回ログイン →
 * 使い始めた」の途中で面が変わり（web の計測 / admin の台帳 / コンソールのログ）、セルフサーブの
 * 効果検証が 1 か所でできない。**申請をコンソールのログイン画面の隣に置く**と、入口から利用まで
 * 同じホストで追え、LP は「説明して console へ送る」だけの面になる。
 *
 * developer-console と trainer が **同じ項目・同じ検証・同じ送り先** で申請を出すための単一の真実:
 * - 項目名は `parseB2BApplication`（`../b2b-applications`）の入力（`org_name` / `use_case` …）と同じ。
 *   フォームの `name` 属性と API の body が同じ語彙なので、変換表を持たない。
 * - 面固有の設問（developer: 参照の形・出典表記・想定 MAU … / trainer: 会員数）は {@link B2B_APPLY_QUESTIONS}。
 *   答えは `attrs` に入り、フォームの `name` も `attrs` のキーと同じ。値は LP のフォームと同じ
 *   （admin の集計で面が変わっても混ざらないように）。
 * - 台帳への書き込みは **kalori-api の named entrypoint `B2BApplyInternal`** に委譲する
 *   （コンソールは ops-db を bind しない）。service binding のサブリクエストは `CF-Connecting-IP`
 *   を持たないので、発信元 IP は `X-Kalori-Client-IP` で転送する（trainer → app-api と同じ約束）。
 *
 * 表示は独立した `@kalori/business/onboarding` が担当する。ここは検証済みの body と結果だけを返す。
 */
import { B2B_APPLICATION_LIMITS, parseB2BApplication, } from "./index.js";
import { INTERNAL_CLIENT_IP_HEADER } from "@kalori/identity/internal";
/**
 * 申請がどの面から来たか（`b2b_applications.source`・migration ops `0006`）。
 * `web` = kalori.jp の LP フォーム（2026-09-06 以前の全件・merchant 線は 2026-10-03 まで）、
 * `console` = 各コンソールの `/apply`。**面は `line` で分かる**ので console を線ごとに分けない。
 */
export const B2B_APPLICATION_SOURCES = ["web", "console"];
/** コンソールが委譲する先。ホスト名は binding のルーティングには使われない（entrypoint 直結）が、パスは正確に。 */
export const B2B_APPLY_INTERNAL_URL = "https://web-api.kalori.jp/internal/b2b/apply";
/** フォームに出す選択肢（`retired` を除く）。 */
export function activeB2BApplyOptions(options) {
    return options.filter((o) => !o.retired);
}
/**
 * 線ごとの面固有の設問（フォームに出す順）。3 線とも申請は各コンソールの `/apply`（merchant は 2026-10-03 に LP から brands.kalori.jp へ移した）。
 *
 * developer の設問は、申請後に送っていた**要件確認のメール**（admin `reply-templates.ts` の
 * `CATALOG_API_INQUIRY_BODY`）で聞いていた点そのもの。フォームで先に答えてもらい、審査前の往復を無くす
 * （2026-09-24〜）。どれも「条件に合うか」「月額がいくらになるか」を決める材料で、
 * 用途（画面・誰に見せるか）は共通項目の `use_case` で聞く。
 * ⚠️ 選択肢の `value` は台帳（`attrs`）に残る語彙。**表示名は変えてよいが value は変えない**（過去の申請が読めなくなる）。
 */
export const B2B_APPLY_QUESTIONS = {
    developer: [
        // 2026-10-16〜: 個人（事業以外の目的）も受ける。個人 = 消費者契約なので、名義・条件・規約の効き方が法人と違う
        // （規約 13 条 4 項・`DEVELOPER_PERSONAL_TERMS`）。それより前の申請にはこの答えが無い。
        {
            key: "applicant_type",
            label: "申請者の区分",
            short: "区分",
            hint: "個人の方は、学習・研究・趣味など事業以外の目的でのご利用に限りお受けしています（事業として使う場合は「個人事業主」をお選びください）。",
            options: [
                { value: "corporation", label: "法人・団体" },
                { value: "sole_proprietor", label: "個人事業主" },
                {
                    value: "individual",
                    label: "個人（学習・研究・趣味など、事業以外の目的）",
                    caution: "個人 = 消費者契約。名義は本人の氏名にし、「個人（非商用）の条件」で承認する（承認メールに規約 5 条の承諾が入る）",
                },
            ],
            required: true,
        },
        {
            key: "reference_mode",
            label: "データの参照のしかた",
            short: "参照の形",
            hint: "API は画面に表示するたびに呼び出す使い方が前提です。応答の保存・蓄積（貴社データベースへの取り込みや共有キャッシュ）は利用規約でお断りしています。末端ユーザーの食事記録として、記録した時点の値を残すことはできます。",
            options: [
                { value: "per_view", label: "表示のたびに API を呼ぶ" },
                {
                    value: "per_view_with_records",
                    label: "表示のたびに API を呼び、食事記録には記録した時点の値を残す",
                    caution: "食事記録の機能 — 規約 4 条の 2 の用途に収まるか、kalori と競合するサービス（5 条）かを確認",
                },
                { value: "import", label: "自社のデータベースに取り込みたい", caution: "取り込みは規約 5 条で不可 — CSV の個別連携の相談か、お断り" },
                { value: "undecided", label: "未定・相談したい" },
            ],
            required: true,
        },
        {
            key: "fields_fit",
            label: "必要なデータ項目",
            short: "返却項目",
            hint: "API が返すのは、商品 ID・チェーン・商品名・カロリーとたんぱく質/脂質/炭水化物・出所区分（チェーン公表値 / 推定値 など）・出典 URL です。",
            options: [
                { value: "enough", label: "この項目で足りる" },
                { value: "more", label: "ほかにも必要な項目がある", caution: "v1 に無い項目の希望 — 下の「ほかに必要な項目」を確認" },
                { value: "undecided", label: "まだ分からない" },
            ],
            required: true,
        },
        {
            key: "fields_extra",
            label: "ほかに必要な項目（任意）",
            short: "ほかに必要な項目",
            placeholder: "例: 食塩相当量、アレルゲン",
            answeredCaution: "v1 に無い項目なら、返せないことを返信で伝える（価格・食塩・JAN は返さない）",
        },
        {
            key: "attribution",
            label: "出典の表記",
            short: "出典表記",
            hint: "データを表示する際は、出典が kalori であることと、商品ページへのリンクを併記いただくのが利用の条件です。",
            options: [
                { value: "ok", label: "併記できる" },
                { value: "difficult", label: "難しい・相談したい", caution: "出典の併記は規約 4 条 2 の条件 — 画面に出せないなら契約できない" },
            ],
            required: true,
        },
        {
            key: "caller",
            label: "API の呼び出し元",
            short: "呼び出し元",
            hint: "API 鍵を守るため、アプリやブラウザから直接は呼び出せません。貴社のサーバを経由してください。",
            options: [
                { value: "server", label: "自社のサーバから" },
                { value: "client", label: "アプリ・ブラウザから直接", caution: "直接の呼び出しは不可（鍵が露出する） — サーバ経由に変えられるか確認" },
                { value: "undecided", label: "未定" },
            ],
            required: true,
        },
        {
            key: "expected_mau",
            label: "想定する月間利用者数（MAU）",
            short: "想定 MAU",
            hint: "お見積もりの前提として伺います。MAU は、API を通じてデータを見た末端ユーザーの月間の人数です。",
            options: [
                { value: "lt100", label: "〜100" },
                { value: "100-1000", label: "100〜1,000" },
                { value: "gt1000", label: "1,000〜" },
            ],
            required: true,
        },
        {
            key: "calls_per_user",
            label: "利用者 1 人あたりの参照回数（1 日）",
            short: "1 人あたりの参照/日",
            options: [
                { value: "lt10", label: "〜10 回" },
                { value: "10-100", label: "10〜100 回" },
                { value: "gt100", label: "100 回を超える", caution: "既定の上限（末端ユーザー 1 人 100 回/日）を超える見込み — 上限を相談" },
                { value: "undecided", label: "未定" },
            ],
            required: true,
        },
        {
            key: "start_timing",
            label: "利用開始の希望時期（任意）",
            short: "希望時期",
            options: [
                { value: "within_1m", label: "1 か月以内" },
                { value: "1-3m", label: "1〜3 か月以内" },
                { value: "undecided", label: "未定・情報収集中" },
            ],
        },
    ],
    trainer: [
        {
            key: "clients",
            label: "食事指導中の会員様の人数",
            short: "会員数",
            options: [
                { value: "none", label: "これから始める" },
                { value: "1-5", label: "1〜5人" },
                { value: "6-20", label: "6〜20人" },
                { value: "21+", label: "21人以上" },
            ],
        },
    ],
    // 2026-10-03〜（brands.kalori.jp/apply）。値は LP のフォーム（B2BApplyForm の choiceKey = inquiry）と同じ語彙
    // — 申請画面が LP から移っても台帳の集計が面で割れないように。
    merchant: [
        {
            key: "inquiry",
            label: "ご相談内容",
            short: "ご相談内容",
            options: [
                // 2026-10-04〜: 招待制の無料パイロットで扱うものだけを選ばせる（merchant-api/ROADMAP.md §🎯）。
                // 未掲載のブランドの掲載希望も受ける — 先にお店を掲載してから招待する（進め方 = merchant-api/ROADMAP.md「未掲載ブランドの掲載希望」）。
                { value: "pilot", label: "掲載中のメニューに、公式の栄養成分値を入れたい" },
                { value: "listing", label: "まだ掲載されていないブランドを掲載したい", caution: "未掲載のブランド — 先にお店を掲載してから招待する（店舗数・メニュー公開の有無は問わない。公式サイトにメニューが無ければ相手からもらう）" },
                { value: "data", label: "掲載内容の誤りを直してほしい" },
                // ↓ いまは無いサービス。過去の申請を読めるように残し、フォームには出さない
                { value: "partnership", label: "公式データを継続連携したい", retired: true },
                { value: "insights", label: "マーケティングデータについて相談したい", retired: true },
                { value: "nutrition_tools", label: "栄養素計算ツールについて相談したい", retired: true },
            ],
            required: true,
        },
        {
            key: "store_count",
            label: "店舗数（任意）",
            short: "店舗数",
            options: [
                { value: "1", label: "1 店舗" },
                { value: "2-9", label: "2〜9 店舗" },
                { value: "10-50", label: "10〜50 店舗" },
                { value: "51+", label: "51 店舗以上" },
            ],
        },
    ],
};
/** 申請の `attrs` を、設問の順に「短い名前・表示名・注記」へ。admin の申請詳細と Slack 通知が使う。 */
export function describeB2BApplyAttrs(line, attrs) {
    const questions = B2B_APPLY_QUESTIONS[line];
    const known = questions
        .filter((q) => attrs[q.key])
        .map((q) => {
        const option = q.options?.find((o) => o.value === attrs[q.key]);
        const caution = q.options ? option?.caution : q.answeredCaution;
        return { key: q.key, label: q.short, value: option?.label ?? attrs[q.key], caution: caution ?? null };
    });
    const rest = Object.entries(attrs)
        .filter(([k]) => !questions.some((q) => q.key === k))
        .map(([key, value]) => ({ key, label: null, value, caution: null }));
    return [...known, ...rest];
}
export function readB2BApplyForm(line, form) {
    const s = (k) => (typeof form[k] === "string" ? form[k].trim() : "");
    const attrs = {};
    for (const q of B2B_APPLY_QUESTIONS[line])
        if (s(q.key))
            attrs[q.key] = s(q.key);
    return {
        email: s("email"),
        org_name: s("org_name"),
        contact_name: s("contact_name"),
        website: s("website"),
        use_case: s("use_case"),
        attrs,
        agree: form.agree === "1" || form.agree === "on" || form.agree === true,
        website_url: s("website_url"),
        sid: s("sid"),
        via: s("via"),
    };
}
/** `parseB2BApplication` の英語エラーを画面の文言へ。未知のものは汎用文（新しい検証を足したらここにも足す）。 */
const FORM_ERROR_MESSAGES = {
    "Invalid email": "メールアドレスを確認してください",
    "org_name is required": "会社名・屋号を入力してください",
    "use_case is required": "用途・ご相談内容を入力してください",
    "Invalid website": "Web サイトの URL を確認してください",
};
const FORM_ERROR_FIELDS = {
    "Invalid email": "email",
    "org_name is required": "org_name",
    "use_case is required": "use_case",
    "Invalid website": "website",
};
export const B2B_APPLY_AGREE_REQUIRED = "利用規約とプライバシーポリシーへの同意が必要です";
/**
 * フォームの値を検証して API の body にする。**検証は API と同じ関数**（`parseB2BApplication`）で行い、
 * ここで通ったものは API でも通る（画面で OK なのに 400 が返る、を作らない）。
 * ⚠️ `required` 属性に頼らない（HTML の検証は簡単に外せる）。同意もここで見る。
 */
export function buildB2BApplyBody(line, values, opts) {
    if (!values.agree)
        return { ok: false, error: B2B_APPLY_AGREE_REQUIRED, field: "agree" };
    // 選択肢に無い値は捨てる（台帳の語彙を増やさない）。必須の設問はここで止める — API は attrs の中身を問わない
    const attrs = {};
    for (const q of B2B_APPLY_QUESTIONS[line]) {
        const raw = values.attrs[q.key] ?? "";
        const value = q.options ? (q.options.some((o) => o.value === raw) ? raw : "") : raw.slice(0, B2B_APPLICATION_LIMITS.attrValue);
        if (value)
            attrs[q.key] = value;
        else if (q.required)
            return { ok: false, error: `「${q.label}」を${q.options ? "選んで" : "入力して"}ください`, field: q.key };
    }
    const body = {
        line,
        email: values.email,
        org_name: values.org_name,
        contact_name: values.contact_name || undefined,
        website: values.website || undefined,
        use_case: values.use_case,
        attrs: Object.keys(attrs).length ? attrs : undefined,
        locale: "ja",
        terms_version: opts.termsVersion,
        website_url: values.website_url || undefined,
    };
    const parsed = parseB2BApplication(body);
    if (!parsed.ok) {
        return { ok: false, error: FORM_ERROR_MESSAGES[parsed.error] ?? "入力内容を確認してください", field: FORM_ERROR_FIELDS[parsed.error] ?? "other" };
    }
    return { ok: true, body, parsed: parsed.value };
}
/** 429（同じ IP から 10 分に 3 件）のときの文言。LP のフォームと同じ案内。 */
export const B2B_APPLY_RATE_LIMIT_MESSAGE = "短時間に申請が続いています。しばらくしてからもう一度お試しください";
export const B2B_APPLY_FAILED_MESSAGE = "申請を送れませんでした。しばらくしてからもう一度お試しください";
/**
 * kalori-api の内部面へ申請を送る。`api` は `[[services]]` の binding（`entrypoint = "B2BApplyInternal"`）。
 * 発信元の IP / UA / 国は**申請者のもの**を転送する（bot 判定と IP クールダウンは API 側にある）。
 */
export async function submitB2BApplication(api, body, ctx) {
    const headers = {
        "Content-Type": "application/json",
        [INTERNAL_CLIENT_IP_HEADER]: ctx.clientIp,
    };
    if (ctx.userAgent)
        headers["User-Agent"] = ctx.userAgent;
    if (ctx.country)
        headers["CF-IPCountry"] = ctx.country;
    let res;
    try {
        res = await api.fetch(B2B_APPLY_INTERNAL_URL, { method: "POST", headers, body: JSON.stringify(body) });
    }
    catch (err) {
        console.error("b2b apply forward error:", err);
        return { ok: false, status: 502, error: B2B_APPLY_FAILED_MESSAGE };
    }
    const json = (await res.json().catch(() => null));
    if (res.ok && json?.success)
        return { ok: true };
    if (res.status === 429)
        return { ok: false, status: 429, error: B2B_APPLY_RATE_LIMIT_MESSAGE };
    if (res.status === 400) {
        return { ok: false, status: 400, error: FORM_ERROR_MESSAGES[json?.error ?? ""] ?? "入力内容を確認してください" };
    }
    console.error("b2b apply forward failed:", res.status, json?.error);
    return { ok: false, status: 502, error: B2B_APPLY_FAILED_MESSAGE };
}
// =============================================================
// 申請画面の計測（2026-09-26〜）
// =============================================================
//
// LP（kalori.jp）の計測は `track_engagement_events` に page = `for-trainers` / `data-partners` で入っている
// （web `lib/engagement.ts` → `POST /api/track`）。申請画面の表示・入力エラー・送信も**同じ表・同じ page**に
// 書き、LP から来た人は LP の訪問 ID（`s`）をそのまま使う — 「LP を見た → CTA → 申請画面 → 送信」を
// 1 つの session_id で追えるようにするため（別の表・別の ID にすると、面をまたぐ比率が出せない）。
//
// 書き込みはコンソールから kalori-api の内部面（`B2BApplyInternal` の `/internal/b2b/apply-event`）へ。
// コンソールは analytics DB を bind しない（申請の送信と同じ経路に乗せる）。
// ⚠️ サーバ側で数えるので JS を実行しないクローラも GET を踏む。UA が bot のものは受け手が捨て、
//    `nav`（`Sec-Fetch-Mode: navigate` = ブラウザの画面遷移）を metadata に残す — 読むときは nav で絞る。
/** kalori-api の内部面（`B2BApplyInternal`）。申請の送信と同じ entrypoint。 */
export const B2B_APPLY_EVENT_INTERNAL_URL = "https://web-api.kalori.jp/internal/b2b/apply-event";
/** 申請画面の計測を書く page（LP の page キーと同じ = LP と 1 本のファネル）。 */
export const B2B_APPLY_FUNNEL_PAGE = {
    developer: "data-partners",
    trainer: "for-trainers",
    merchant: "for-restaurants",
};
/**
 * 申請画面のイベント。LP の `view` / `submit` と名前を分ける（同じ page に書くので、混ぜると LP の閲覧数が膨らむ）。
 * - `apply_view` 申請画面の表示（GET /apply）
 * - `apply_error` 送信したが入力で止まった（metadata.field = どこで）
 * - `apply_submit` 受け付けた（台帳 `b2b_applications` の行と 1:1 ではない — 受け手の bot 判定で捨てた分も入る）
 */
export const B2B_APPLY_EVENTS = ["apply_view", "apply_error", "apply_submit"];
const SESSION_ID_RE = /^[A-Za-z0-9-]{8,64}$/;
const VIA_RE = /^[a-z0-9-]{1,40}$/;
/**
 * 申請画面の訪問 ID と導線を決める。LP の CTA が付けた `s`（LP の訪問 ID）と `via`（CTA 名）があれば使い、
 * 無ければ新しい ID を振る（直接来た人・ブックマーク）。形が崩れた値は捨てる（任意の文字列を表に入れない）。
 */
export function b2bApplyTracking(input) {
    const s = input.s ?? "";
    const via = input.via && VIA_RE.test(input.via) ? input.via : "";
    if (SESSION_ID_RE.test(s))
        return { sid: s, via, fromLp: true };
    return { sid: crypto.randomUUID(), via, fromLp: false };
}
export function isB2BApplyEventBody(v) {
    if (!v || typeof v !== "object")
        return false;
    const b = v;
    return ((b.line === "developer" || b.line === "trainer" || b.line === "merchant") &&
        B2B_APPLY_EVENTS.includes(String(b.event)) &&
        typeof b.session_id === "string" &&
        SESSION_ID_RE.test(b.session_id) &&
        !!b.metadata &&
        typeof b.metadata === "object");
}
/**
 * 申請画面のイベントを送る。**失敗は握りつぶす**（計測で申請を落とさない）。呼び出し側は waitUntil に渡す。
 * UA と IP は申請者のものを転送する（受け手が bot を捨て、ip_hash を LP の行と同じ作り方で出す）。
 */
export async function recordB2BApplyEvent(api, body, ctx) {
    const headers = { "Content-Type": "application/json", [INTERNAL_CLIENT_IP_HEADER]: ctx.clientIp };
    if (ctx.userAgent)
        headers["User-Agent"] = ctx.userAgent;
    try {
        await api.fetch(B2B_APPLY_EVENT_INTERNAL_URL, { method: "POST", headers, body: JSON.stringify(body) });
    }
    catch (err) {
        console.error("b2b apply event forward error:", err);
    }
}
//# sourceMappingURL=client.js.map